import { useMemo, useState, ReactNode } from 'react';
import {
  MessagesSquare,
  ShieldAlert,
  Search,
  X,
  Loader2,
  Trash2,
  Skull,
  ShieldOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  useConversationDetailQuery,
  useConversationListQuery,
  useConversationMessagesQuery,
} from '@/client/api/messaging/messaging.query';
import {
  usePurgeConversationHardMutation,
  usePurgeConversationMutation,
  useSetConversationBlockMutation,
} from '@/client/api/messaging/messaging.mutation';
import {
  AdminMessage,
  ConversationListParams,
  ConversationRegistry,
  entityFromKey,
  formatEpoch,
  formatRelativeEpoch,
  inquiryVisitorName,
  isInquiryMessage,
  STATUS_BADGE,
} from '@/client/types/messaging';
import { useListFilters } from '@/client/hooks/useListFilters';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import Pagination from '@/client/components/common/Pagination';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import {
  ConversationParties,
  MessageParties,
} from '@/client/components/admin/Messaging/PartyHoverCard';

interface ThreadFilters {
  scope: string;
  key_prefix: string;
  sort: string;
  order: string;
}

const selectCls =
  'min-w-[130px] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Viewing threads requires the{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">message.view</code> permission.
    </p>
  </div>
);

const InfoRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div>
    <p className="mb-0.5 text-xs text-gray-500">{label}</p>
    <div className="break-all text-sm font-medium text-gray-900">{value ?? '—'}</div>
  </div>
);

const ThreadDrawer = ({
  conversationKey,
  isOpen,
  setIsOpen,
  canModerate,
  canDelete,
  onClose,
}: {
  conversationKey: string | null;
  isOpen: boolean;
  setIsOpen: (o: boolean) => void;
  canModerate: boolean;
  canDelete: boolean;
  onClose: () => void;
}) => {
  const [reason, setReason] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const { data: detail, isLoading: detailLoading } = useConversationDetailQuery(
    isOpen ? conversationKey : null,
  );
  const { data: messagesData, isLoading: msgsLoading } = useConversationMessagesQuery(
    isOpen ? conversationKey : null,
    { include_deleted: includeDeleted ? 'true' : 'false', limit: 100 },
  );
  const softPurge = usePurgeConversationMutation();
  const hardPurge = usePurgeConversationHardMutation();
  const setBlock = useSetConversationBlockMutation();

  if (!isOpen) return null;

  const messages = messagesData?.messages ?? [];
  const busy = softPurge.isPending || hardPurge.isPending || setBlock.isPending;
  const key = conversationKey || '';
  const blocked = detail?.registry?.blocked ?? null;

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent className="mx-auto flex max-h-[92vh] max-w-3xl flex-col">
        <DrawerHeader className="border-b border-gray-100 text-left">
          <DrawerTitle className="flex items-center gap-2">
            <MessagesSquare className="h-5 w-5 text-blue-600" />
            {detailLoading ? (
              'Loading…'
            ) : (
              <ConversationParties
                conversation={{
                  conversation_key: key,
                  user_summaries: detail?.user_summaries,
                  org_summaries: detail?.org_summaries,
                }}
              />
            )}
          </DrawerTitle>
          <DrawerDescription>
            <span className="font-mono text-xs">{key}</span>
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {detailLoading && <Skeleton className="h-20 w-full" />}
          {!detailLoading && detail && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <InfoRow label="Total" value={detail.counts?.total ?? '—'} />
              <InfoRow label="Active" value={detail.counts?.active ?? '—'} />
              <InfoRow label="Deleted" value={detail.counts?.deleted ?? '—'} />
              <InfoRow label="Unread" value={detail.counts?.unread ?? '—'} />
            </div>
          )}
          {blocked && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              Blocked for a policy violation{blocked.at ? ` on ${formatEpoch(blocked.at)}` : ''}
              {blocked.reason ? `: ${blocked.reason}` : ''}. Participants cannot send.
            </p>
          )}

          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-800">Messages</h3>
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={includeDeleted}
                onChange={(e) => setIncludeDeleted(e.target.checked)}
              />
              Include deleted
            </label>
          </div>

          <div className="space-y-2">
            {msgsLoading &&
              Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
            {!msgsLoading && messages.length === 0 && (
              <p className="py-8 text-center text-sm text-gray-500">No messages in this thread</p>
            )}
            {!msgsLoading &&
              messages.map((msg: AdminMessage) => {
                const inquiry = isInquiryMessage(msg);
                return (
                  <div
                    key={msg.id || msg.hash}
                    className={`rounded-lg border p-3 ${
                      msg.is_deleted
                        ? 'border-red-100 bg-red-50/40'
                        : inquiry
                          ? 'border-amber-100 bg-amber-50/50'
                          : 'border-gray-100 bg-white'
                    }`}
                  >
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <MessageParties msg={msg} className="text-xs" />
                      {inquiry && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                          Inquiry
                        </span>
                      )}
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                          STATUS_BADGE[msg.status ?? 'sent'] ?? STATUS_BADGE.sent
                        }`}
                      >
                        {msg.status ?? 'sent'}
                      </span>
                      <span className="text-[11px] text-gray-400">
                        {formatEpoch(msg.timestamp ?? msg.created_at)}
                      </span>
                    </div>
                    <p className="whitespace-pre-wrap text-sm text-gray-900">
                      {msg.message || (msg.redacted ? '[redacted]' : '—')}
                    </p>
                    {inquiry && (
                      <p className="mt-1 text-xs text-amber-800">
                        {inquiryVisitorName(msg)}
                        {msg.attachment?.visitor_email
                          ? ` · ${msg.attachment.visitor_email}`
                          : ''}
                        {msg.attachment?.visitor_phone
                          ? ` · ${msg.attachment.visitor_phone}`
                          : ''}
                        {' — reply via email/phone'}
                      </p>
                    )}
                  </div>
                );
              })}
          </div>

          {(canModerate || canDelete) && (
            <div>
              <label className="mb-1 block text-xs text-gray-500">
                Reason (required to block or unblock, optional for purge)
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. spam thread…"
              />
            </div>
          )}
        </div>

        <DrawerFooter className="border-t border-gray-100">
          <div className="flex flex-wrap gap-2">
            {canModerate && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy || !reason.trim() || !detail?.registry}
                onClick={async () => {
                  try {
                    await setBlock.mutateAsync({ key, blocked: !blocked, reason: reason.trim() });
                    toast.success(blocked ? 'Conversation unblocked' : 'Conversation blocked', {
                      richColors: true,
                    });
                    setReason('');
                  } catch (error: any) {
                    toast.error(error?.message || 'Block update failed', { richColors: true });
                  }
                }}
              >
                {setBlock.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <ShieldOff className="mr-1.5 h-4 w-4" />
                )}
                {blocked ? 'Unblock conversation' : 'Block conversation'}
              </Button>
            )}
            {canModerate && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={async () => {
                  try {
                    await softPurge.mutateAsync({
                      key,
                      reason: reason || undefined,
                      idempotencyKey: generateIdempotencyKey(),
                    });
                    toast.success('Thread soft-purged', { richColors: true });
                  } catch (error: any) {
                    toast.error(error?.message || 'Soft-purge failed', { richColors: true });
                  }
                }}
              >
                {softPurge.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="mr-1.5 h-4 w-4" />
                )}
                Soft-purge
              </Button>
            )}
            {canDelete && (
              <Button
                type="button"
                size="sm"
                variant="destructive"
                disabled={busy}
                onClick={async () => {
                  if (
                    !window.confirm(
                      'Hard-purge deletes all messages and the registry row. This cannot be undone.',
                    )
                  )
                    return;
                  try {
                    await hardPurge.mutateAsync({
                      key,
                      reason: reason || undefined,
                      idempotencyKey: generateIdempotencyKey(),
                    });
                    toast.success('Thread hard-purged', { richColors: true });
                    onClose();
                  } catch (error: any) {
                    toast.error(error?.message || 'Hard-purge failed', { richColors: true });
                  }
                }}
              >
                {hardPurge.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Skull className="mr-1.5 h-4 w-4" />
                )}
                Hard-purge
              </Button>
            )}
            <DrawerClose asChild>
              <Button type="button" variant="ghost" size="sm" onClick={onClose}>
                Close
              </Button>
            </DrawerClose>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

const MessagingThreadsPage = () => {
  useBreadcrumbs([
    { label: 'Communications', href: '/admin/communications/messaging' },
    { label: 'Messaging' },
    { label: 'Threads' },
  ]);

  const viewPerm = useHasPermission('message.view');
  const moderatePerm = useHasPermission('message.moderate');
  const deletePerm = useHasPermission('message.delete');

  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canModerate = moderatePerm.isSuperAdmin || moderatePerm.hasPermission;
  const canDelete = deletePerm.isSuperAdmin || deletePerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const {
    searchTerm,
    debouncedSearchTerm,
    filters,
    page,
    limit,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    resetFilters,
  } = useListFilters<ThreadFilters>({
    initialFilters: { scope: '', key_prefix: '', sort: 'last_message_ts', order: 'desc' },
    initialLimit: 25,
  });

  const listParams: ConversationListParams = useMemo(() => {
    const q = debouncedSearchTerm.trim();
    const asId = q && /^\d+$/.test(q) ? Number(q) : undefined;
    const params: ConversationListParams = {
      limit,
      offset: (page - 1) * limit,
      sort: filters.sort || 'last_message_ts',
      order: filters.order || 'desc',
      ...(filters.key_prefix ? { key_prefix: filters.key_prefix } : {}),
    };
    if (q.includes(':') && !q.endsWith(':')) {
      params.conversation_key = q;
    } else if (asId != null) {
      if (filters.scope === 'oid') params.oid = asId;
      else params.uid = asId;
    }
    return params;
  }, [debouncedSearchTerm, filters, page, limit]);

  const { data, isLoading, isError, error } = useConversationListQuery(
    listParams,
    canView && !permLoading,
  );

  const conversations = data?.conversations ?? [];
  const pagination = data?.pagination;
  const forbidden =
    (error as { status?: number })?.status === 403 || (!permLoading && !canView);
  const hasFilters =
    !!searchTerm ||
    !!filters.scope ||
    !!filters.key_prefix ||
    filters.sort !== 'last_message_ts' ||
    filters.order !== 'desc';

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <AccessDenied />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div>
          <div className="mb-1 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              <MessagesSquare className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-2xl font-black text-gray-900">Threads</h1>
          </div>
          <p className="text-sm text-gray-500">
            Browse conversation registry rows and moderate entire threads.
          </p>
        </div>

        <div className="rounded-lg bg-white p-4 shadow">
          <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap">
            <div className="relative min-w-[200px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="UID / OID or conversation_key…"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filters.scope}
              onChange={(e) => handleFilterChange('scope', e.target.value)}
              className={selectCls}
            >
              <option value="">Numeric → UID</option>
              <option value="oid">Numeric → OID</option>
            </select>
            <select
              value={filters.key_prefix}
              onChange={(e) => handleFilterChange('key_prefix', e.target.value)}
              className={selectCls}
            >
              <option value="">All key types</option>
              <option value="userpair:">userpair:</option>
              <option value="orguser:">orguser:</option>
              <option value="orgpair:">orgpair:</option>
              <option value="platform:">platform:</option>
            </select>
            <select
              value={filters.sort}
              onChange={(e) => handleFilterChange('sort', e.target.value)}
              className={selectCls}
            >
              <option value="last_message_ts">Sort: Last activity</option>
              <option value="created_ts">Sort: Created</option>
              <option value="last_seq">Sort: Last seq</option>
            </select>
            <select
              value={filters.order}
              onChange={(e) => handleFilterChange('order', e.target.value)}
              className={selectCls}
            >
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>
            {hasFilters && (
              <button
                type="button"
                onClick={resetFilters}
                className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                <X className="h-4 w-4" />
                Clear
              </button>
            )}
          </div>
        </div>

        {isError && !forbidden && (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {(error as Error)?.message || 'Failed to load threads'}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr className="bg-gray-50">
                  {['Thread', 'Key', 'Entity', 'Last activity', 'Seq'].map((h) => (
                    <th
                      key={h}
                      className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoading &&
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 5 }).map((__, j) => (
                        <td key={j} className="px-6 py-4">
                          <Skeleton className="h-4 w-20" />
                        </td>
                      ))}
                    </tr>
                  ))}
                {!isLoading && conversations.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-16 text-center text-sm text-gray-500">
                      No conversations found
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  conversations.map((c: ConversationRegistry) => {
                    const eid = entityFromKey(c.conversation_key);
                    return (
                      <tr
                        key={c.conversation_key}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => {
                          setSelectedKey(c.conversation_key);
                          setIsDrawerOpen(true);
                        }}
                      >
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          <ConversationParties conversation={c} />
                        </td>
                        <td className="max-w-xs truncate px-6 py-4 font-mono text-xs text-gray-500">
                          {c.conversation_key}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {eid ? (
                            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                              Listing #{eid}
                            </span>
                          ) : (
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                              Direct
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatRelativeEpoch(c.last_message_ts)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{c.last_seq ?? '—'}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-gray-100 px-6 py-3">
            <Pagination
              total={pagination?.total ?? 0}
              page={page}
              itemsPerPage={limit}
              limit={limit}
              setPage={handlePageChange}
              setItemsPerPage={handleLimitChange}
              showButtons
              showSelect
              selectOptions={[10, 25, 50, 100]}
            />
          </div>
        </div>
      </div>

      <ThreadDrawer
        conversationKey={selectedKey}
        isOpen={isDrawerOpen}
        setIsOpen={setIsDrawerOpen}
        canModerate={canModerate}
        canDelete={canDelete}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedKey(null);
        }}
      />
    </div>
  );
};

export default MessagingThreadsPage;
